import * as React from "react"
import { Center, Spinner, useTheme } from "@chakra-ui/react"
import { useDeepCompareMemo } from "use-deep-compare"
import { useGetMapBikesQuery } from "../lib/hooks/queries/useGetMapBikesQuery"
import { useMapBBox } from "./providers/MapBBoxProvider"
import { MapProps } from "./BikesMapMapbox"
import GMap from "../gmaps/GMap"
import ZoneLayer from "../gmaps/ZoneLayer"
import { useThresholdsColorsGetter } from "../lib/hooks/useThresholdsColors"
import BikesMapGoogleSupercluster from "./BikesMapGoogleSupercluster"
import { useFillOpacityGetter } from "../lib/hooks/useDropZoneStyle"

const MAP_ID = "bikes-gmap"

const BikesMapGoogleMap: React.FC<MapProps> = ({
  selectedZone,
  showServiceZones,
  serviceZones,
  showDropzones,
  dropzones,
  showMaintenanceZones,
  maintenanceZones,
  search,
  bikeStates,
  bikeModels,
  bikeTags,
  handleSearchUpdate,
  activeBike,
}) => {
  const { currentGMapBounds, currentBBox } = useMapBBox()

  const theme = useTheme()

  const getThresholdColors = useThresholdsColorsGetter()

  const { bikes, bikesLoading } = useGetMapBikesQuery({
    bbox: currentBBox,
    search,
    bikeStates,
    bikeModels,
    bikeTags,
  })

  const memoBikes = useDeepCompareMemo(() => bikes?.filter((b) => !!b.longitude && !!b.latitude), [bikes])

  React.useEffect(
    () => handleSearchUpdate({ bikes: memoBikes, bikesLoading }),
    [handleSearchUpdate, memoBikes, bikesLoading],
  )

  const getFillOpacity = useFillOpacityGetter()

  return (
    <GMap
      mapId={MAP_ID}
      containerStyle={{
        height: "100%",
        width: "100%",
      }}
      initialBounds={currentGMapBounds}
      shareCurrentView
    >
      {/* ---------- ZONES ----------- */}
      {/* dropzones */}
      <ZoneLayer
        mapId={MAP_ID}
        zones={dropzones}
        showZones={showDropzones}
        properties={{
          zoneType: "dropzone",
        }}
        layerStyle={(feature: google.maps.Data.Feature) => {
          const minThresh = feature.getProperty("low_threshold") as number
          const maxThresh = feature.getProperty("high_threshold") as number
          const val = feature.getProperty("bicycle_count") as number
          const fillColor = getThresholdColors({ minThresh, maxThresh, val }) ?? theme.colors.blue["500"]
          const uuid = feature.getProperty("uuid") as string
          const isSelected = selectedZone?.uuid === uuid || false
          return {
            fillColor,
            fillOpacity: getFillOpacity({ isSelected }),
            strokeColor: isSelected ? theme.colors.pink["500"] : theme.colors.blue["800"],
            strokeWeight: isSelected ? 2 : 1,
          }
        }}
      />

      {/* cycling_area zones (aka serviceZones) */}
      <ZoneLayer
        mapId={MAP_ID}
        zones={serviceZones}
        showZones={showServiceZones}
        properties={{
          zoneType: "cycling_area",
        }}
        layerStyle={{
          fillColor: theme.colors.purple["500"],
          strokeColor: theme.colors.purple["800"],
          strokeWeight: 1,
          fillOpacity: getFillOpacity(),
        }}
      />

      {/* maintenance zones */}
      <ZoneLayer
        mapId={MAP_ID}
        zones={maintenanceZones}
        showZones={showMaintenanceZones}
        properties={{
          zoneType: "maintenance",
        }}
        layerStyle={{
          fillColor: theme.colors.gray["500"],
          fillOpacity: getFillOpacity(),
          strokeColor: theme.colors.gray["800"],
          strokeWeight: 1,
        }}
      />

      {/* ---------- BIKES ----------- */}
      {bikesLoading && (
        <Center pos="absolute" w="100%" bottom={8}>
          <Center p={2} bg="white" borderRadius="md" h={50} w={50}>
            <Spinner size="sm" />
          </Center>
        </Center>
      )}

      <BikesMapGoogleSupercluster mapId={MAP_ID} bikes={memoBikes} activeBike={activeBike} />
    </GMap>
  )
}

export default BikesMapGoogleMap
